import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Edit3, Check, X, Sparkles } from "lucide-react";
import { FAQ } from "@/types/article";
import { pb } from "@/lib/pocketbase";
import { toast } from "sonner";

interface ArticleFAQProps {
  faqs: FAQ[];
  onFAQsChange: (faqs: FAQ[]) => void;
  brandId?: string;
  keyword?: string;
  outline?: any;
}

export function ArticleFAQ({ faqs, onFAQsChange, brandId, keyword, outline }: ArticleFAQProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const addFAQ = () => {
    const newFAQ: FAQ = {
      question: "",
      answer: "",
    };
    const newIndex = faqs.length;
    onFAQsChange([...faqs, newFAQ]);
    setEditingIndex(newIndex);
    setEditQuestion("");
    setEditAnswer("");

    // Scroll to the new FAQ after it's added
    setTimeout(() => {
      cardRefs.current[newIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const deleteFAQ = (index: number) => {
    const updatedFAQs = faqs.filter((_, i) => i !== index);
    onFAQsChange(updatedFAQs);
    // Reset editing state if deleting the currently edited FAQ
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditQuestion("");
      setEditAnswer("");
    } else if (editingIndex !== null && editingIndex > index) {
      // Adjust editing index if deleting an FAQ before the currently edited one
      setEditingIndex(editingIndex - 1);
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditQuestion(faqs[index].question);
    setEditAnswer(faqs[index].answer);
  };

  const saveEdit = () => {
    if (editingIndex !== null) {
      const updatedFAQs = [...faqs];
      if (!editQuestion.trim() && !editAnswer.trim()) {
        deleteFAQ(editingIndex);
      } else {
        updatedFAQs[editingIndex] = {
          question: editQuestion,
          answer: editAnswer,
        };
        onFAQsChange(updatedFAQs);
      }
      setEditingIndex(null);
      setEditQuestion("");
      setEditAnswer("");
    }
  };

  const cancelEdit = () => {
    if (editingIndex !== null) {
      const currentFAQ = faqs[editingIndex];
      // If both question and answer are empty, delete the FAQ
      if (!currentFAQ.question.trim() && !currentFAQ.answer.trim()) {
        deleteFAQ(editingIndex);
        return;
      }
    }
    setEditingIndex(null);
    setEditQuestion("");
    setEditAnswer("");
  };

  const generateFAQs = async () => {
    if (!brandId || !keyword || !outline) {
      toast.error("Missing required data to generate FAQs");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await pb.send("/api/v1/posts/generateFAQ", {
        method: "POST",
        body: {
          brandId,
          keyword,
          outline,
        },
      });

      if (response.success && response.data) {
        // Assuming the API returns FAQs in the format: { faqs: [{ question: string, answer: string }] }
        const generatedFAQs = response.data.faqs || response.data;
        if (Array.isArray(generatedFAQs)) {
          onFAQsChange([...faqs, ...generatedFAQs]);
          toast.success("FAQs generated successfully!");
        } else {
          toast.error("Invalid FAQ data received from server");
        }
      } else {
        toast.error("Failed to generate FAQs");
      }
    } catch (error) {
      console.error("Error generating FAQs:", error);
      toast.error("Failed to generate FAQs");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
        <div className="flex gap-2">
          {brandId && keyword && outline && faqs.length < 6 && (
            <Button 
              onClick={generateFAQs} 
              variant="default" 
              size="sm"
              disabled={isGenerating}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {isGenerating ? "Generating..." : "Generate FAQs"}
            </Button>
          )}
          <Button onClick={addFAQ} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add FAQ
          </Button>
        </div>
      </div>

      {faqs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <p>
              No FAQs added yet. Click "Add FAQ" to get started
              {brandId && keyword && outline && faqs.length < 6 && ', or use "Generate FAQs" for AI suggestions'}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <Card key={index} ref={(el) => (cardRefs.current[index] = el)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  {editingIndex === index ? (
                    <div className="flex-1 space-y-3">
                      <Input
                        value={editQuestion}
                        onChange={(e) => setEditQuestion(e.target.value)}
                        placeholder="Enter question..."
                        className="font-medium"
                      />
                      <Textarea
                        value={editAnswer}
                        onChange={(e) => setEditAnswer(e.target.value)}
                        placeholder="Enter answer..."
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button onClick={saveEdit} size="sm" variant="default">
                          <Check className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button
                          onClick={cancelEdit}
                          size="sm"
                          variant="outline"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-2">
                          {faq.question || "Untitled Question"}
                        </CardTitle>
                        <p className="text-muted-foreground">
                          {faq.answer || "No answer provided"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => startEditing(index)}
                          size="sm"
                          variant="outline"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteFAQ(index)}
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
